import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	selectCurrencies,
	getCurrenciesAsync,
	getCurrencyAsync,
	createCurrencyAsync,
	selectCurrency,
	selectEditing,
	setEditing,
	removeCurrency,
	updateCurrencyAsync,
} from '../../redux/currencySlice';
import { selectCurrentTab, updateTabStatus } from '../../redux/tabsSlice';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { blueGrey } from '@mui/material/colors';
import CancelIcon from '@mui/icons-material/Cancel';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { Grid } from '@mui/material';
import { createAlertAsync } from '../../redux/alertsSlice';
import CircularProgress from '@mui/material/CircularProgress';
import _ from 'lodash';
import SaveIcon from '@mui/icons-material/Save';
import Backdrop from '@mui/material/Backdrop';
import CreatedModified from '../shared/CreatedModified';

export default function Currencies() {
	const dispatch = useDispatch();
	const currentTab = useSelector(selectCurrentTab);
	const currencies = useSelector(selectCurrencies);
	const editing = useSelector(selectEditing());
	const currency = useSelector(selectCurrency());
	const [currencyName, setCurrencyName] = useState('');
	const [addModalOpen, setAddModal] = useState(false);
	const [dialogueModalOpen, setDialogueModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const handleAddModal = () => setAddModal(!addModalOpen);
	const handleDialogueModal = () => setDialogueModal(!dialogueModalOpen);

	useEffect(() => {
		if (!currencies) {
			dispatch(getCurrenciesAsync());
		}
	}, [currencies, dispatch]);

	useEffect(() => {
		if (_.isEqual(editing, currency)) {
			dispatch(updateTabStatus({ id: currentTab.id, status: true }));
		} else {
			dispatch(updateTabStatus({ id: currentTab.id, status: false }));
		}
	}, [currentTab, editing, currency, dispatch]);

	useEffect(() => {
		if (editing) {
			setLoading(false);
		}
	}, [editing, currencies]);

	function handleChange(event) {
		setCurrencyName(event.target.value);
	}

	function handleEdit(event) {
		dispatch(
			setEditing({ name: event.target.name, value: event.target.value })
		);
	}

	function addCurrency(e) {
		e.preventDefault();

		const exitingCurrency = currencies.find(
			(currency) => currency.Name === currencyName
		);

		if (!exitingCurrency) {
			dispatch(createCurrencyAsync(currencyName));
			setCurrencyName('');
			handleAddModal();
		} else {
			dispatch(
				createAlertAsync({
					message: 'Currency already exists',
					severity: 'error',
					autoDismiss: true,
					timeout: 6000,
				})
			);
		}
	}

	function getCurrency(id) {
		if (!editing || (editing && _.isEqual(currency, editing))) {
			dispatch(removeCurrency());
			setLoading(true);
			dispatch(getCurrencyAsync(id));
		} else {
			handleDialogueModal();
		}
	}

	function stopEditing() {
		if (_.isEqual(currency, editing)) {
			setLoading(false);
			dispatch(removeCurrency(editing._id));
		} else {
			handleDialogueModal();
		}
	}

	function updateCurrency(e) {
		e.preventDefault();
		setLoading(true);
		dispatch(updateCurrencyAsync(editing));
	}

	function discardChanges() {
		setLoading(false);
		dispatch(removeCurrency());
		handleDialogueModal();
	}

	let saveColor = _.isEqual(currency, editing) ? 'green' : 'red';

	return (
		<Box>
			<Box sx={{ textAlign: 'right', marginBottom: 2, marginRight: 1 }}>
				<Tooltip title='Add Currency'>
					<IconButton onClick={handleAddModal}>
						<AddCircleIcon
							sx={{
								color: blueGrey[600],
								backgroundColor: blueGrey[100],
								borderRadius: '5px',
								fontSize: '1.5em',
							}}
						/>
					</IconButton>
				</Tooltip>
			</Box>
			<Box
				sx={{
					display: 'flex',
				}}
			>
				<Box
					sx={{
						flexGrow: 1,
						backgroundColor: blueGrey[100],
						color: blueGrey[900],
						borderRadius: '5px',
						margin: 2,
						paddingTop: 4,
						paddingBottom: 4,
						textAlign: 'center',
						alignItems: 'center',
						width: '15em',
						minWidth: '15em',
						maxWidth: '15em',
						minHeight: '400px',
					}}
				>
					{currencies && (
						<Box>
							<Box sx={{ color: blueGrey[900], marginBottom: 1 }}>ACTIVE</Box>
							{currencies.map(
								(currency) =>
									currency.IsActive === true && (
										<Box
											key={currency._id}
											sx={{ textAlign: 'center' }}
										>
											<Button
												variant='contained'
												onClick={() => getCurrency(currency._id)}
												sx={{
													minWidth: '10em',
													textTransform: 'capitalize',
													marginBottom: 2,
													backgroundColor: currency.IsActive
														? blueGrey[800]
														: blueGrey[500],
													color: currency.IsActive
														? blueGrey[50]
														: blueGrey[900],
													'&:hover': {
														backgroundColor: blueGrey[900],
													},
												}}
											>
												{currency.Name}
											</Button>
										</Box>
									)
							)}
						</Box>
					)}
					{currencies && (
						<Box>
							<Box sx={{ color: blueGrey[900], marginTop: 4, marginBottom: 1 }}>
								INACTIVE
							</Box>
							{currencies.map(
								(currency) =>
									currency.IsActive === false && (
										<Box
											key={currency._id}
											sx={{ textAlign: 'center' }}
										>
											<Button
												variant='contained'
												onClick={() => getCurrency(currency._id)}
												sx={{
													minWidth: '10em',
													textTransform: 'capitalize',
													marginBottom: 2,
													backgroundColor: currency.IsActive
														? blueGrey[800]
														: blueGrey[500],
													color: currency.IsActive
														? blueGrey[50]
														: blueGrey[900],
													'&:hover': {
														backgroundColor: blueGrey[900],
													},
												}}
											>
												{currency.Name}
											</Button>
										</Box>
									)
							)}
						</Box>
					)}
					{!currencies && (
						<Box>
							<CircularProgress />
						</Box>
					)}
				</Box>
				<Box
					sx={{
						flexGrow: 8,
						backgroundColor: blueGrey[100],
						color: blueGrey[900],
						borderRadius: '10px',
						margin: 2,
						paddingTop: 2,
						paddingBottom: 2,
						visibility: `${editing || loading ? 'visible' : 'hidden'}`,
						minWidth: '500px',
					}}
				>
					{editing && !loading ? (
						<>
							<Box
								component={'form'}
								onSubmit={updateCurrency}
								sx={{ textAlign: 'right', marginRight: 4 }}
							>
								<Tooltip title='Save Currency'>
									<IconButton
										onClick={updateCurrency}
										sx={{ marginRight: 2 }}
									>
										<SaveIcon
											style={{
												color: saveColor,
												borderRadius: '5px',
												fontSize: '1.5em',
											}}
										/>
									</IconButton>
								</Tooltip>
								<Tooltip title='Close'>
									<IconButton onClick={stopEditing}>
										<CancelIcon
											sx={{
												color: blueGrey[600],
												borderRadius: '5px',
												fontSize: '1.5em',
											}}
										/>
									</IconButton>
								</Tooltip>
							</Box>
							<Grid
								container
								spacing={2}
								sx={{ marginTop: 6 }}
							>
								<Grid
									sm={6}
									item
								>
									<TextField
										key='IsActive'
										select
										required
										variant='standard'
										label='Is Active'
										value={editing.IsActive}
										name='IsActive'
										onChange={handleEdit}
										sx={{ width: '50%' }}
									>
										<MenuItem value={true}>TRUE</MenuItem>
										<MenuItem value={false}>FALSE</MenuItem>
									</TextField>
								</Grid>
								<Grid
									sm={6}
									item
								>
									<TextField
										key='Name'
										required
										variant='standard'
										label='Currency Name'
										value={editing.Name}
										name='Name'
										onChange={handleEdit}
										sx={{
											width: '80%',
											'& .MuiInputBase-input': {
												textAlign: 'center',
											},
										}}
									/>
								</Grid>
							</Grid>
							<CreatedModified account={editing} />
						</>
					) : (
						<Box
							sx={{
								textAlign: 'center',
								alignItems: 'center',
								marginTop: 15,
								flexGrow: 20,
							}}
						>
							<CircularProgress
								size={60}
								color='primary'
								sx={{
									color: `${blueGrey[800]} !important`,
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<Modal
				open={addModalOpen}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 1000,
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: blueGrey[50],
						border: '2px solid #000',
						boxShadow: 24,
						p: 4,
					}}
				>
					<Box
						component={'form'}
						onSubmit={addCurrency}
					>
						<TextField
							key='Name'
							required
							variant='standard'
							label='Currency Name'
							value={currencyName}
							name='Name'
							onChange={handleChange}
							//sx={textFieldStyles}
						/>
						<Stack
							spacing={2}
							direction='row'
							sx={{ marginTop: '3em' }}
						>
							<Button
								variant='contained'
								onClick={handleAddModal}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Cancel
							</Button>
							<Button
								variant='contained'
								type='submit'
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Save
							</Button>
						</Stack>
					</Box>
				</Box>
			</Modal>
			<Modal
				open={dialogueModalOpen}
				closeAfterTransition
				BackdropComponent={Backdrop}
				BackdropProps={{
					timeout: 1000,
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: blueGrey[50],
						border: '2px solid #000',
						boxShadow: 24,
						p: 4,
					}}
				>
					<Box
						sx={{
							textAlign: 'center',
							fontSize: '1.25em',
							color: blueGrey[900],
						}}
					>
						<Box>There are unsaved changes.</Box>
						<Box>How would you like to proceed?</Box>
						<Stack
							spacing={2}
							direction='row'
							sx={{ marginTop: '3em' }}
						>
							<Button
								variant='contained'
								onClick={handleDialogueModal}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Cancel & Go Back
							</Button>
							<Button
								variant='contained'
								onClick={discardChanges}
								sx={{
									flexGrow: 1,
									backgroundColor: blueGrey[500],
									'&:hover': { backgroundColor: blueGrey[600] },
								}}
							>
								Discard Changes
							</Button>
						</Stack>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
}
